<script lang="ts">
	import { Tooltip } from 'bits-ui';
	import LogOut from '@lucide/svelte/icons/log-out';
	import Monitor from '@lucide/svelte/icons/monitor';
	import Moon from '@lucide/svelte/icons/moon';
	import Sun from '@lucide/svelte/icons/sun';

	import Logo from '$lib/components/Logo.svelte';
	import { DESTINATIONS, isCurrent, type Destination } from '$lib/domain/nav';
	import { apply, nextTheme, stored, themeLabel, type Theme } from '$lib/domain/theme';

	interface Props {
		pathname: string;
		/** Absent until the session loads; the account controls appear with it. */
		email?: string;
		onSignOut: () => void;
	}

	let { pathname, email, onSignOut }: Props = $props();

	// Read once on mount rather than from a load function: it lives in localStorage, which
	// only exists in the browser, and app.html has already applied it to <html> by now.
	let theme = $state<Theme>('system');
	$effect(() => {
		theme = stored();
	});

	const ICONS = { system: Monitor, light: Sun, dark: Moon };
	const ThemeIcon = $derived(ICONS[theme]);
	const nextMode = $derived(nextTheme(theme));

	function cycleTheme() {
		theme = nextMode;
		apply(theme);
	}

	// Tooltip ids have to be unique per instance and stable across renders — Bits UI needs a
	// real id to point aria-describedby at (see `tip` below).
	const uid = $props.id();
	const tipId = (name: string) => `${uid}-${name.toLowerCase().replace(/\W+/g, '-')}`;
</script>

<!--
	Bits UI 2.19 puts neither an id nor `role="tooltip"` on the content element, and wires the
	trigger's aria-describedby to `contentNode.id` — so left alone it resolves to "" and the
	label is never announced. Owning both elements through the `child` snippet is what closes
	that loop, and it keeps the markup ours so Svelte's scoped CSS survives portalling.

	`side="right"` even in the mobile bottom bar: Floating UI flips on collision, so the
	tooltip lands somewhere sensible either way, and it is moot on touch where nothing hovers.
-->
{#snippet tip(id: string, text: string)}
	<Tooltip.Portal>
		<Tooltip.Content {id} side="right" sideOffset={8}>
			{#snippet child({ props, wrapperProps })}
				<!-- Floating UI computes the wrapper's styles; only the inner box is ours. -->
				<div {...wrapperProps}>
					<div {...props} {id} role="tooltip" class="tooltip">{text}</div>
				</div>
			{/snippet}
		</Tooltip.Content>
	</Tooltip.Portal>
{/snippet}

{#snippet destination(item: Destination)}
	{@const { href, label, icon: Icon } = item}
	{@const current = isCurrent(item, pathname)}
	{@const id = tipId(label)}
	<Tooltip.Root>
		<Tooltip.Trigger>
			{#snippet child({ props })}
				<!-- The icon is decorative; aria-label carries the name, so the link is never
					 announced as bare punctuation when the tooltip is closed. -->
				<a
					{...props}
					{href}
					class:current
					aria-current={current ? 'page' : undefined}
					aria-label={label}
				>
					<Icon size={20} aria-hidden="true" />
				</a>
			{/snippet}
		</Tooltip.Trigger>
		{@render tip(id, label)}
	</Tooltip.Root>
{/snippet}

<nav class="rail" aria-label="Primary">
	<a class="mark" href="/" aria-label="SLA Reports — home">
		<Logo size={30} />
	</a>

	<!-- Centred rather than stacked under the mark: three icons hard against the top of a
		 tall rail read as the leftovers of a header, and the eye has to travel to a corner to
		 find them. In the middle they are the rail's subject. -->
	<div class="group main">
		{#each DESTINATIONS as item (item.href)}
			{@render destination(item)}
		{/each}
	</div>

	<div class="group foot">
		<!--
			One cycling button rather than three side by side: the rail is one icon wide and a
			segmented control does not fit in it. The cost of a cycle is that reaching a
			particular mode can take two clicks; what it must not also cost is not knowing
			where you are, so the button shows the *current* mode and the tooltip names both it
			and what one more click does.
		-->
		<Tooltip.Root>
			<Tooltip.Trigger>
				{#snippet child({ props })}
					<button
						{...props}
						type="button"
						onclick={cycleTheme}
						aria-label="Colour theme: {themeLabel(theme)}"
					>
						<ThemeIcon size={20} aria-hidden="true" />
					</button>
				{/snippet}
			</Tooltip.Trigger>
			{@render tip(tipId('theme'), `Theme: ${themeLabel(theme)} — click for ${themeLabel(nextMode)}`)}
		</Tooltip.Root>

		{#if email}
			<!-- The address rides in the tooltip: the rail has no room for it as text, and
				 which account you are signed in as is worth being able to check. -->
			<Tooltip.Root>
				<Tooltip.Trigger>
					{#snippet child({ props })}
						<button {...props} type="button" onclick={onSignOut} aria-label="Sign out">
							<LogOut size={20} aria-hidden="true" />
						</button>
					{/snippet}
				</Tooltip.Trigger>
				{@render tip(tipId('signout'), `Sign out — ${email}`)}
			</Tooltip.Root>
		{/if}
	</div>
</nav>

<style>
	.rail {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 4.5rem;
		padding: var(--space-md) 0;
		/* Sticky, because the review screen is long and Approve lives at the top of it.
		   `align-self: start` is what makes that work in the shell's grid — stretched to the
		   row's full height there would be nothing for it to stick within. */
		position: sticky;
		top: 0;
		align-self: start;
		height: 100dvh;
	}

	.mark {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 3rem;
		height: 3rem;
		flex: none;
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-sm);
		flex: none;
	}

	/* Equal auto margins above and below are what centre the destinations in the space the
	   mark and the foot leave, without either of them having to know the other's height. */
	.group.main {
		margin-block: auto;
	}

	/* One shape for every item, links and buttons alike. The mark is excluded: it draws its
	   own filled tile and would otherwise sit in a second, larger one. */
	.rail a:not(.mark),
	.rail button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 3rem;
		height: 3rem;
		padding: 0;
		border: 0;
		border-radius: var(--radius-lg);
		background: var(--color-raised);
		color: var(--color-muted);
		cursor: pointer;
		text-decoration: none;
		transition:
			background-color 120ms ease,
			color 120ms ease;
	}

	.rail a:not(.mark):hover,
	.rail button:hover {
		color: var(--color-text);
	}

	/* The only filled thing in the rail, so where you are is readable at a glance rather than
	   by comparing two shades of grey. Naming it for view transitions makes the tile travel
	   between destinations instead of blinking out and back in — and because exactly one
	   element carries the name at a time, the browser pairs them up on its own. */
	.rail a.current {
		background: var(--color-primary);
		color: var(--color-primary-contrast);
		box-shadow: var(--shadow-sm);
		view-transition-name: nav-active;
	}

	.tooltip {
		max-width: 32ch;
		padding: var(--space-xs) var(--space-sm);
		border-radius: var(--radius-sm);
		background: var(--color-text);
		color: var(--color-background);
		font-size: 0.75rem;
		line-height: 1.45;
	}

	/* Under 46rem the rail would take a sixth of a phone's width, so it becomes a bottom bar
	   — within thumb reach, and the content gets the full width back. Same items, same
	   shapes, same active tile. */
	@media (max-width: 46rem) {
		.rail {
			position: fixed;
			inset: auto 0 0 0;
			z-index: 10;
			flex-direction: row;
			justify-content: center;
			gap: var(--space-md);
			width: auto;
			height: auto;
			padding: var(--space-sm) var(--space-md);
			padding-bottom: calc(var(--space-sm) + env(safe-area-inset-bottom));
			border-top: 1px solid var(--color-border);
			background: color-mix(in srgb, var(--color-background) 82%, transparent);
			backdrop-filter: blur(10px);
		}

		/* The mark is the one thing that goes: it identifies the app, which the app itself is
		   already doing by being on screen, and Reports gets you home anyway. */
		.mark {
			display: none;
		}

		.group {
			flex-direction: row;
			gap: var(--space-sm);
		}

		/* The centring is a vertical idea; in a row it would push the two groups apart to
		   opposite ends of the bar. */
		.group.main {
			margin-block: 0;
		}
	}
</style>
